/**
 * SectionRowCard — Shared visual shell for section rows in FlexibleLayout.
 *
 * Renders:
 *   - Top bar: [optional drag handle] [label/edit + pencil] [spacer] [delete icon]
 *   - Card: [type chip] [optional extra chips] [spacer] [settings gear]
 *   - Collapse: caller-provided content via `renderExpandedContent`
 *
 * Used by:
 *   - SortableCompactRow (top-level sections: drag + InlineSectionEditor)
 *   - EmbedChildSectionsManager (embed data sources: no drag + FlexibleSectionContentAdapter)
 */

import React, { useCallback, useState } from 'react';
import {
  Box,
  Chip,
  Collapse,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import TuneIcon from '@mui/icons-material/Tune';
import type { LayoutSection } from '@segmently/schema-models/v2';
import { WithGuideHelp } from '@modules/ui-components';
import { useEditorGuideService } from '@modules/reactFlow/contexts';

// ─── Chip config ─────────────────────────────────────────────────────────────

type ChipColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';

const SECTION_KIND_LABELS: Record<string, string> = {
  Text: 'Text',
  Media: 'Media',
  OptionsList: 'Options List',
  SingleSelectionList: 'Selection List',
  MultipleSelectionList: 'Selection List',
  BulletList: 'Bullet List',
  CustomEmbed: 'Custom Embed',
  ProductCatalog: 'Product Catalog',
  PaywallHeader: 'Paywall Header',
  TextField: 'Text Field',
  Button: 'Button',
  Header: 'Header Bar',
  Footer: 'Action Bar',
  ArticleIndex: 'Article index',
};

const SECTION_KIND_COLORS: Record<string, ChipColor> = {
  Text: 'default',
  Media: 'secondary',
  OptionsList: 'info',
  SingleSelectionList: 'info',
  MultipleSelectionList: 'info',
  BulletList: 'info',
  CustomEmbed: 'warning',
  TextField: 'warning',
  Button: 'error',
  ProductCatalog: 'success',
  PaywallHeader: 'success',
  Header: 'primary',
  Footer: 'error',
  ArticleIndex: 'info',
};

/** Detect purchase buttons by action.kind === 'purchase' on the TapTarget */
function isPurchaseButton(section: LayoutSection): boolean {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (section as any).buttonContent?.action?.kind === 'purchase';
}

/** Get display label for a section */
export function getSectionDisplayLabel(section: LayoutSection): string {
  if (section.kind === 'Button' && isPurchaseButton(section)) {
    return 'Purchase Button';
  }
  return SECTION_KIND_LABELS[section.kind] || section.kind;
}

/** Get chip color for a section */
export function getSectionChipColor(section: LayoutSection): ChipColor {
  if (section.kind === 'Button' && isPurchaseButton(section)) {
    return 'success';
  }
  return SECTION_KIND_COLORS[section.kind] || 'default';
}

/** Get chip label for a section */
export function getSectionChipLabel(section: LayoutSection): string {
  if (section.kind === 'Button' && isPurchaseButton(section)) {
    return 'Purchase';
  }
  return section.kind;
}

// ─── Props ───────────────────────────────────────────────────────────────────

export interface SectionRowCardProps {
  section: LayoutSection;
  /** Display label (falls back to kind label) */
  displayLabel?: string;
  /** Called when label changes via inline edit */
  onLabelChange?: (sectionId: string, newLabel: string) => void;
  /** Called to request section deletion */
  onDeleteRequest: (sectionId: string, label: string) => void;
  /** Disables all interactions */
  disabled?: boolean;
  /** Optional drag handle element (rendered at the left of top bar) */
  dragHandle?: React.ReactNode;
  /** Optional extra chips between type chip and spacer (e.g. parent link) */
  extraChips?: React.ReactNode;
  /** Content rendered inside the collapse panel */
  renderExpandedContent?: () => React.ReactNode;
  /** Outer wrapper sx (for margin, opacity, etc.) */
  outerSx?: Record<string, unknown>;
  /** Optional per-control guide keys. Omitted for generic/data-source usages. */
  guideKeys?: {
    reorder?: string;
    sectionName?: string;
    deleteSection?: string;
    openSectionSettings?: string;
  };
}

// ─── Component ───────────────────────────────────────────────────────────────

export const SectionRowCard: React.FC<SectionRowCardProps> = ({
  section,
  displayLabel: displayLabelProp,
  onLabelChange,
  onDeleteRequest,
  disabled = false,
  dragHandle,
  extraChips,
  renderExpandedContent,
  outerSx,
  guideKeys,
}) => {
  const guideService = useEditorGuideService();
  const [settingsExpanded, setSettingsExpanded] = useState(false);
  const [isEditingLabel, setIsEditingLabel] = useState(false);
  const [editLabelValue, setEditLabelValue] = useState('');

  const displayLabel = displayLabelProp ?? section.label ?? getSectionDisplayLabel(section);

  const handleSettingsToggle = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setSettingsExpanded((prev) => !prev);
  }, []);

  const handleEditStart = useCallback((e: React.MouseEvent) => {
    e.stopPropagation();
    setEditLabelValue(displayLabel);
    setIsEditingLabel(true);
  }, [displayLabel]);

  const handleEditConfirm = useCallback(() => {
    const trimmed = editLabelValue.trim();
    if (trimmed && trimmed !== displayLabel && onLabelChange) {
      onLabelChange(section.id, trimmed);
    }
    setIsEditingLabel(false);
  }, [editLabelValue, displayLabel, onLabelChange, section.id]);

  const handleEditCancel = useCallback(() => {
    setEditLabelValue(displayLabel);
    setIsEditingLabel(false);
  }, [displayLabel]);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleEditConfirm();
    }
    if (e.key === 'Escape') {
      handleEditCancel();
    }
  }, [handleEditConfirm, handleEditCancel]);

  const withGuide = useCallback(
    (
      guideKey: string | undefined,
      children: React.ReactNode,
      position: 'top-right' | 'top-left' | 'inline-end' = 'top-right',
    ) => {
      if (!guideKey || !guideService) return children;
      return (
        <WithGuideHelp
          guideKey={guideKey}
          guideService={guideService}
          data-testid={`guide-anchor-${guideKey}`}
          position={position}
          iconSize="small"
          stopClickPropagation
        >
          {children}
        </WithGuideHelp>
      );
    },
    [guideService],
  );

  return (
    <Box data-testid={`section-row-${section.id}`} sx={{ mb: 1.5, ...outerSx }}>
      {/* ── Top bar: [drag?] + Label + Delete ── */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.5,
          px: 0.5,
          py: 0.25,
        }}
      >
        {/* Optional drag handle */}
        {withGuide(guideKeys?.reorder, dragHandle, 'inline-end')}

        {/* Label — view/edit toggle */}
        {isEditingLabel ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flex: 1, minWidth: 0 }}>
            <TextField
              size="small"
              value={editLabelValue}
              onChange={(e) => setEditLabelValue(e.target.value)}
              onKeyDown={handleKeyDown}
              onFocus={(e) => e.target.select()}
              autoFocus
              sx={{
                flex: 1,
                '& .MuiInputBase-input': { fontSize: '0.75rem', padding: '2px 6px' },
              }}
            />
            <IconButton size="small" onClick={handleEditConfirm} color="primary" disabled={!editLabelValue.trim()}>
              <CheckIcon sx={{ fontSize: 14 }} />
            </IconButton>
            <IconButton size="small" onClick={handleEditCancel}>
              <CloseIcon sx={{ fontSize: 14 }} />
            </IconButton>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary' }} noWrap>
              {displayLabel}
            </Typography>
            {!disabled && onLabelChange && (
              <>
                {withGuide(
                  guideKeys?.sectionName,
                  (
                    <IconButton size="small" onClick={handleEditStart} sx={{ p: 0.25, ml: 0.5 }} data-testid={`section-row-edit-${section.id}`}>
                      <EditIcon sx={{ fontSize: 14 }} />
                    </IconButton>
                  ),
                  'top-right',
                )}
              </>
            )}
          </Box>
        )}

        {/* Delete — plain icon */}
        {withGuide(
          guideKeys?.deleteSection,
          (
            <Box
              data-testid={`section-row-delete-${section.id}`}
              component="span"
              onClick={disabled ? undefined : (e: React.MouseEvent) => {
                e.stopPropagation();
                onDeleteRequest(section.id, displayLabel);
              }}
              sx={{
                display: 'flex',
                cursor: disabled ? 'default' : 'pointer',
                color: 'text.secondary',
                opacity: disabled ? 0.3 : 0.7,
                '&:hover': disabled ? {} : { color: 'error.main', opacity: 1 },
              }}
            >
              <DeleteIcon sx={{ fontSize: 18 }} />
            </Box>
          ),
          'top-left',
        )}
      </Box>

      {/* ── Card: Type chip + Settings gear ── */}
      <Box
        sx={{
          borderRadius: 1,
          border: '1px solid',
          borderColor: settingsExpanded ? 'primary.main' : 'divider',
          bgcolor: 'background.paper',
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 2,
            minHeight: 48,
          }}
        >
          <Chip
            label={getSectionChipLabel(section)}
            size="small"
            color={getSectionChipColor(section)}
            sx={{ fontSize: 10, height: 20, flexShrink: 0 }}
          />

          {extraChips}

          <Box sx={{ flex: 1 }} />

          {/* Settings gear icon */}
          {renderExpandedContent && (
            <>
              {withGuide(
                guideKeys?.openSectionSettings,
                (
                  <IconButton
                    data-testid={`section-row-settings-${section.id}`}
                    aria-expanded={settingsExpanded}
                    aria-controls={`section-row-content-${section.id}`}
                    size="small"
                    disabled={disabled}
                    onClick={handleSettingsToggle}
                    sx={{
                      color: settingsExpanded ? 'primary.main' : 'action.active',
                      '&:hover': { color: 'primary.main' },
                    }}
                  >
                    <TuneIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                ),
                'top-left',
              )}
            </>
          )}
        </Box>

        {/* Collapse panel */}
        {renderExpandedContent && (
          <Collapse in={settingsExpanded} timeout="auto" unmountOnExit>
            <Box id={`section-row-content-${section.id}`} sx={{ px: 1.5, pb: 1.5, pt: 0.5 }}>
              {renderExpandedContent()}
            </Box>
          </Collapse>
        )}
      </Box>
    </Box>
  );
};
